import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import swal from 'sweetalert2';
import { SalesService } from 'src/app/services/sales.service';
import { ToastService } from 'src/app/shared-new/services/toast.service';

/** A row as the list renders it. */
export interface ClientNameRow {
  viewerName: string;
  /** This vet's clinic subscription has declared this name before. */
  usedBefore: boolean;
}

@Component({
  selector: 'app-disclose-client-popup',
  templateUrl: './disclose-client-popup.component.html',
  styleUrls: ['./disclose-client-popup.component.scss'],
})
export class DiscloseClientPopupComponent {
  @Output() onSave = new EventEmitter<any>();
  @Input() data: { saleId: number; horseId: number; saleHorse: any; clientName: string };

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  /** Every name this clinic has declared before. */
  allClientNames: ClientNameRow[] = [];

  /** Names shown, after the search filter. Split for display only. */
  usedBeforeRows: ClientNameRow[] = [];
  otherRows: ClientNameRow[] = [];

  searchText = '';
  isLoading = true;

  /**
   * False when ThoroughAdmin has switched this clinic's client name list off for this sale's
   * sales company: no suggestions are shown and the vet types the name.
   */
  listEnabled = true;
  selectedViewer: ClientNameRow | null = null;

  /**
   * Keyboard cursor, held as the row itself rather than an index.
   *
   * The template compares against this on every row, so it must be an O(1) field read. An
   * earlier draft exposed `visibleRows` and `totalMatchCount` as getters and compared
   * `visibleRows[activeIndex] === item` inside the *ngFor — that rebuilt the array and
   * re-filtered the full list on every change-detection pass, for every row.
   */
  activeRow: ClientNameRow | null = null;

  /** Flat list in render order, recomputed only when the filter changes. */
  visibleRows: ClientNameRow[] = [];

  /** Matches before the display cap, so the template can report what it is not showing. */
  totalMatchCount = 0;
  hiddenCount = 0;

  /** normalised name -> row, for O(1) exact-match checks from the template. */
  private nameLookup = new Map<string, ClientNameRow>();

  /**
   * Rows rendered per section before the list is capped.
   *
   * The previous implementation sliced the whole list to 50 with no indication, so any name
   * past the 50th alphabetically was simply invisible and users had no way to know. The cap
   * is kept — a sale can carry hundreds of names — but it is now per section, far larger, and
   * the template states plainly when it bites.
   */
  readonly displayLimit = 250;

  private searchSubject = new Subject<string>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private salesService: SalesService
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(200), takeUntil(this.unsubscribe$))
      .subscribe(() => this.updateDisplayList());

    this.loadClientNames();
  }

  ngAfterViewInit(): void {
    // Focus the search box so the vet can type immediately.
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  // ---------------------------------------------------------------------------- loading

  /**
   * The list is this clinic's OWN declared client names and nothing else.
   *
   * It used to be TwAuthApi GET /repository/{saleId}/saleViewers (UspViewersActiveInSale),
   * which returns every client name any clinic declared in this sale and the sales company's
   * two preceding sales. That showed a vet who had viewed nothing the clients of ~17 other
   * practices (prod, Arqana Arc sale 2622, 2026-10-01). Declared client names are
   * vet-generated data and stay per clinic.
   *
   * The names are scoped on MemberId (the clinic subscription), so colleagues on the same
   * subscription share names and nobody else's appear. It fails closed to an empty list; the
   * vet can still type a new name.
   *
   * clientNameList also reports whether the clinic's list is switched off (ThoroughAdmin) for
   * this sale's sales company; then nothing is suggested at all.
   */
  loadClientNames(): void {
    this.isLoading = true;

    this.salesService
      .getClientNameList(this.data?.saleId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ enabled, names: mine }) => {
          this.listEnabled = enabled;
          const seen = new Set<string>();
          this.allClientNames = (enabled ? mine ?? [] : [])
            .map((name) => (name ?? '').trim())
            .filter((name) => {
              const key = this.normalise(name);
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map((name) => ({ viewerName: name, usedBefore: true }));

          this.nameLookup = new Map(
            this.allClientNames.map((row) => [this.normalise(row.viewerName), row])
          );

          this.updateDisplayList();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.toastService.error(
            'Could not load client names. You can still type a new one below.',
            'Client names unavailable'
          );
        },
      });
  }

  // ---------------------------------------------------------------------------- filtering

  /** Trim + case-fold. Both sides of every comparison go through this. */
  private normalise(value: string): string {
    return (value ?? '').trim().toLowerCase();
  }

  public updateDisplayList(): void {
    const search = this.normalise(this.searchText);

    const matches = search
      ? this.allClientNames.filter((r) => this.normalise(r.viewerName).includes(search))
      : this.allClientNames;

    // The vet's own names first — that is the whole point of the tag, and it is the single
    // biggest time-saver when a sale carries hundreds of names.
    this.usedBeforeRows = matches.filter((r) => r.usedBefore).slice(0, this.displayLimit);
    this.otherRows = matches.filter((r) => !r.usedBefore).slice(0, this.displayLimit);

    this.visibleRows = [...this.usedBeforeRows, ...this.otherRows];
    this.totalMatchCount = matches.length;
    this.hiddenCount = Math.max(0, matches.length - this.visibleRows.length);

    // Keep any selection that is still visible; drop it otherwise so Confirm cannot act on a
    // row the vet can no longer see.
    if (this.selectedViewer && !matches.some((r) => this.isSameName(r, this.selectedViewer!))) {
      this.selectedViewer = null;
    }

    this.activeRow = this.visibleRows.length ? this.visibleRows[0] : null;
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchText);
  }

  /** Stable identity for *ngFor so re-filtering does not rebuild every row. */
  trackByName(_index: number, row: ClientNameRow): string {
    return row.viewerName;
  }

  // ---------------------------------------------------------------------------- selection

  private isSameName(a: ClientNameRow, b: ClientNameRow): boolean {
    return this.normalise(a.viewerName) === this.normalise(b.viewerName);
  }

  selectViewer(viewer: ClientNameRow): void {
    this.selectedViewer = viewer;
  }

  isSelected(viewer: ClientNameRow): boolean {
    return !!this.selectedViewer && this.isSameName(viewer, this.selectedViewer);
  }

  clearSelection(): void {
    this.selectedViewer = null;
  }

  /**
   * The typed text already exists in the list, so there is nothing to create.
   *
   * Backed by a lookup map rather than a scan: this getter is reached from the template via
   * disableConfirm() and confirmTargetName several times per change-detection pass, and a
   * linear find over ~1,400 names each time adds up for no reason. It stays a getter (not a
   * field recomputed in updateDisplayList) so the Confirm button reacts to typing immediately
   * instead of lagging behind the 200 ms search debounce.
   */
  get exactMatch(): ClientNameRow | null {
    const search = this.normalise(this.searchText);
    if (!search) return null;
    return this.nameLookup.get(search) ?? null;
  }

  get trimmedSearch(): string {
    return (this.searchText ?? '').trim();
  }

  /** Offer "create" only for text that is not already a name in the list. */
  get canCreateNew(): boolean {
    return this.trimmedSearch.length > 0 && this.exactMatch === null;
  }

  // ---------------------------------------------------------------------------- keyboard

  onSearchKeydown(event: KeyboardEvent): void {
    const rows = this.visibleRows;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!rows.length) return;

      const current = this.activeRow ? rows.indexOf(this.activeRow) : -1;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (current + step + rows.length) % rows.length;

      this.activeRow = rows[next];
      this.scrollActiveIntoView();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      // Enter commits the highlighted row. With no rows (a brand-new name) it falls through
      // to whatever Confirm would do with the typed text.
      if (this.activeRow) this.selectViewer(this.activeRow);
      if (!this.disableConfirm()) this.save();
    }
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      document
        .querySelector('.client-modal__item.is-active')
        ?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  // ---------------------------------------------------------------------------- confirm

  /**
   * Enabled when there is something unambiguous to confirm: an explicit selection, text that
   * exactly names an existing client, or text to create as a new name.
   *
   * The previous version disabled Confirm whenever the list had any results and no row had
   * been clicked — so typing a client's full name correctly still left the button dead.
   */
  disableConfirm(): boolean {
    if (this.selectedViewer) return false;
    if (this.exactMatch) return false;
    if (this.canCreateNew) return false;
    return true;
  }

  /** What Confirm will actually submit, so the button can say so. */
  get confirmTargetName(): string {
    if (this.selectedViewer) return this.selectedViewer.viewerName;
    if (this.exactMatch) return this.exactMatch.viewerName;
    return this.trimmedSearch;
  }

  save(): void {
    // Precedence matters. The previous implementation tested "no results and text typed"
    // FIRST, so selecting a client and then typing something unmatched silently discarded the
    // selection and created a new name from the search box instead. An explicit selection now
    // always wins.
    const name = this.confirmTargetName;

    if (!name) {
      swal.fire('Warning', 'Client Name is missing', 'warning');
      return;
    }

    this.onSave.emit({ viewerName: name, saleHorseId: this.data.saleHorse.saleHorseId });
    this.activeModal.close(name);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
